<template>
  <div class="counter">
    <button @click="decrement" class="counter-btn counter-btn_decrement">
      <VisuallyHidden>
        <div slot="shown">-</div>
        <div slot="hidden">Decrease Quantity</div>
      </VisuallyHidden>
    </button>
    <input
      id="quantity"
      class="counter-count"
      type="number"
      v-model.number="componentValue"
      v-on:input="callLeadTime"
    />

    <button @click="increment" class="counter-btn counter-btn_increment">
      <VisuallyHidden>
        <div slot="shown">+</div>
        <div slot="hidden">Increase Quantity</div>
      </VisuallyHidden>
    </button>
  </div>
</template>

<script>
import VisuallyHidden from "../shared/VisuallyHidden";
import { mapActions, mapGetters } from "vuex";

export default {
  mounted() {
    this.updateValidCartCount(this.getMinimumAndIncrementQuantity);
    this.componentValue = this.getMinimumAndIncrementQuantity;
  },

  data() {
    return {
      componentValue: undefined,
      debounce: null
    };
  },
  methods: {
    ...mapActions("addToCartCount", [
      "updateErrorStates",
      "updateValidCartCount"
    ]),
    increment() {
      let updatedValue = this.componentValue;
      // if keyboard user entered an invalid quantity, then increments

      const { value } = this.fixInputIfInvalid(updatedValue);
      updatedValue = value;

      // updates the value to be updated
      updatedValue += this.getMinimumAndIncrementQuantity;

      // updates count, also checks and fixes too high, too low
      this.validateAndUpdateCount(updatedValue);

      // Call the lead time func for updated lead time based on qty
      this.callLeadTime();
    },
    decrement() {
      let updatedValue = this.componentValue;
      // if keyboard user entered an invalid quantity, then decrements
      const { value, isNotAdjusted } = this.fixInputIfInvalid(updatedValue);
      updatedValue = value;

      // calculates the value to be updated
      if (isNotAdjusted) {
        updatedValue -= this.getMinimumAndIncrementQuantity;
      }

      // updates count, also checks and fixes too high, too low
      this.validateAndUpdateCount(updatedValue);

      // Call the lead time func for updated lead time based on qty
      this.callLeadTime();
    },
    fixInputIfInvalid(val) {
      let valueIsNotAdjusted = true;
      if (this.isInvalidQuantity()) {
        // subtracts the remainder to make the update value valid
        valueIsNotAdjusted = false;
        val -= this.getQuantityRemainder();
      }
      return {
        value: val,
        isNotAdjusted: valueIsNotAdjusted
      };
    },
    validateAndUpdateCount(val) {
      if (this.isAboveMax(val)) {
        this.componentValue = this.cartMax;
      }
      // below the minimum so value set to min
      if (this.isBelowMin(val)) {
        this.componentValue = this.getMinimumAndIncrementQuantity;
      }
      // checks if updatedValue is above minimum, then updates
      if (this.isAboveMin(val) && this.isBelowMax(val)) {
        this.componentValue = val;
      }
      return val;
    },
    handleErrorLogic(value) {
      const tooHigh = !this.isBelowMax(value);
      const tooLow = !this.isAboveMin(value);
      const justRight = !tooHigh && !tooLow;
      const invalidQuantity = this.isInvalidQuantity(value);
      // if user has removed number from input
      const emptyInputValue = typeof value == "string";

      let updatedErrorMsg = this.defaultHelpText;

      if (tooHigh) {
        updatedErrorMsg = this.inputTooHigh;
      }
      if (tooLow) {
        updatedErrorMsg = this.inputTooLow;
      }
      if (justRight && invalidQuantity) {
        updatedErrorMsg = this.inputInvalidIncrement;
      }
      if (emptyInputValue) {
        updatedErrorMsg = this.inputEmpty;
      }
      if (justRight & !invalidQuantity) {
        this.updateValidCartCount(value);
      }
      this.updateErrorStates(updatedErrorMsg);
    },
    isAboveMin(value) {
      return value >= this.getMinimumAndIncrementQuantity;
    },
    isBelowMin(value) {
      return value < this.getMinimumAndIncrementQuantity;
    },
    isAboveMax(value) {
      return value > this.cartMax;
    },
    isBelowMax(value) {
      return value <= this.cartMax;
    },
    getQuantityRemainder() {
      return this.componentValue % this.getMinimumAndIncrementQuantity;
    },
    isInvalidQuantity() {
      return this.getQuantityRemainder() !== 0;
    },
    callLeadTime() {
      if (
        this.componentValue !== "" &&
        this.componentValue !== 0 &&
        this.componentValue !== null &&
        this.componentValue !== undefined
      ) {
        clearTimeout(this.debounce);
        this.debounce = setTimeout(() => {
          this.$store.dispatch("product/updateLeadTime", this.$store);
          this.$store.dispatch("product/setIsLeadTimeLoading", true);
        }, 3500);
      }
    }
  },
  computed: {
    ...mapGetters("addToCartCount", [
      "getMinimumAndIncrementQuantity",
      "cartMax",
      "getMOQ"
    ]),
    inputTooHigh() {
      return `Invalid quantity. Must be less than or equal to ${this.cartMax}.`;
    },
    inputTooLow() {
      return `Invalid quantity. Must be greater than or equal to ${this.getMinimumAndIncrementQuantity}.`;
    },
    inputInvalidIncrement() {
      return `Invalid quantity. Must be increments of ${this.getMinimumAndIncrementQuantity}.`;
    },
    inputEmpty() {
      return `Invalid quantity. Must enter a quantity.`;
    },
    defaultHelpText() {
      if (this.getMinimumAndIncrementQuantity == 1 && this.getMOQ != 1) {
        return `Must be ordered with a quantity greater than ${this.getMOQ}`;
      } else if (this.getMOQ == 1 && this.getMinimumAndIncrementQuantity != 1) {
        return `Must be ordered in increments of ${this.getMinimumAndIncrementQuantity}`;
      } else if (this.getMOQ != 1 && this.getMinimumAndIncrementQuantity != 1) {
        return `Must be ordered in increments of ${this.getMinimumAndIncrementQuantity}`;
      } else {
        return "";
      }
    }
  },
  watch: {
    componentValue(value) {
      this.handleErrorLogic(value);
    },
    getMinimumAndIncrementQuantity(newMin, oldMin) {
      if (newMin && newMin !== oldMin) {
        this.componentValue = newMin;
      }
    }
  },
  components: { VisuallyHidden }
};
</script>

<style lang="scss" scoped>
.counter {
  display: flex;
  align-items: center;
  justify-content: center;
  //   color: $neutral;
  border-radius: 0;
  //   border: 1px solid $lightgray;
  border: 1px solid gray;
  width: 110px;
  //   @media screen and (max-width: $media-max-width-tablet) {
  //     width: 150px;
  //   }
  input[type="number"]::-webkit-outer-spin-button,
  input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type="number"] {
    -moz-appearance: textfield;
  }
}

.counter-btn {
  width: 30px;
  height: 47px;
  padding: 7px 0 10px;
  //   border: 0 solid $lightgray;
  border: 0 solid gray;
  //   background-color: $white;
  background-color: white;
  //   color: $lightgray;
  color: $black85 !important;
  font-size: 16px !important;
  cursor: pointer;
  //   font-family: $heading;
  font-family: $body;
}

.counter-btn_decrement {
  border-right-width: 1px;
}

.counter-btn_increment {
  border-left-width: 1px;
}

// important needed to override main.css input font-size important
input.counter-count {
  width: 50px;
  height: 47px;
  font-size: 16px !important;
  text-align: center;
  border: 0;
  outline: 0;
  font-family: $body;
  padding: 0;
  color: $black85 !important;
  font-weight: normal !important;
}
</style>
